/**
 * Spec 014b US-5: age choices on a site whose brand sets a minimum driver age
 * (`myBrandRules.effectiveMinimumAge`). Tested in `brandAge.test.ts`.
 */

export interface DriverAgeOption {
  /** Sent as `driverAge`: the youngest age in the range. */
  value: number;
  label: string;
}

const RANGES: ReadonlyArray<[number, number | null]> = [
  [18, 20],
  [21, 24],
  [25, null],
];

/**
 * The guest's age ranges from the site's minimum: ranges below it are gone and
 * a range that starts below it starts at it (21 → "21-24", 23 → "23-24").
 */
export function driverAgeOptions(minimumAge = 18): DriverAgeOption[] {
  return RANGES.filter(([, upper]) => upper === null || upper >= minimumAge).map(
    ([lower, upper]) => {
      const from = Math.max(lower, minimumAge);
      const label = upper === null ? `${from}+` : from === upper ? `${from}` : `${from}-${upper}`;
      return { value: from, label };
    },
  );
}

/** A remembered or URL age raised to the site's minimum. */
export function atLeastMinimumAge(age: number | null | undefined, minimumAge = 18): number | null {
  if (age === null || age === undefined || Number.isNaN(age)) return null;
  return Math.max(age, minimumAge);
}

/**
 * The age limits a host may pick for a car: 18, 21 and 25 from the brand's
 * floor up, plus the floor itself when it is none of them.
 */
export function carAgeLimitOptions(floor: number | null | undefined): number[] {
  const minimum = floor ?? 18;
  const options = [18, 21, 25].filter(age => age >= minimum);
  return options.includes(minimum) ? options : [minimum, ...options];
}
